import { useTranslation } from 'react-i18next';
import { TrendingUp, FileText } from 'lucide-react';
import { PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, LineChart, Line, CartesianGrid, AreaChart, Area } from 'recharts';
import { useOwnerDashboardData } from './useOwnerDashboardData';
import { Select } from '@/components/ui/Select';

function fmt(n: number | null | undefined) {
  const v = isNaN(Number(n)) ? 0 : (Number(n) ?? 0);
  return '₹' + v.toLocaleString('en-IN');
}

function fmtShort(n: number | null | undefined) {
  const v = isNaN(Number(n)) ? 0 : (Number(n) ?? 0);
  if (v >= 100000) return '₹' + (v / 100000).toFixed(1) + 'L';
  if (v >= 1000) return '₹' + (v / 1000).toFixed(1) + 'k';
  return '₹' + v.toLocaleString('en-IN');
}

export function OwnerAnalyticsPage() {
  const { t } = useTranslation();

  const {
    hostels, isLoading,
    activeHostels, multiHostel, selectedId, setSelectedId, isAll, activeHostelId,
    s, totalToCollect, revenueData, occupancyTrendData, pending,
  } = useOwnerDashboardData();

  const occupancyData = [
    { name: t('dashboard.occupiedBeds'), value: s.occupiedBeds, color: '#0d9488' },
    { name: t('common.reserved'), value: s.reservedBeds, color: '#f59e0b' },
    { name: t('dashboard.blockedBeds'), value: s.blockedBeds, color: '#6b7280' },
    { name: t('dashboard.vacantBeds'), value: s.vacantBeds, color: '#22c55e' },
  ];

  if (!isLoading && hostels.length === 0) {
    return (
      <div className="max-w-7xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">{t('dashboard.detailedMetrics')}</h1>
        <p className="text-sm text-gray-500 mb-6">{t('dashboard.detailedMetricsSubtitle')}</p>
        <div className="bg-white rounded-xl p-10 text-center border border-dashed border-gray-200">
          <p className="text-gray-400">{t('hostel.noHostels')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('dashboard.detailedMetrics')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('dashboard.detailedMetricsSubtitle')}</p>
        </div>
        {multiHostel && (
          <Select
            value={selectedId}
            onChange={e => setSelectedId(e.target.value)}
            className="w-auto"
            options={[
              { value: 'ALL', label: t('dashboard.allHostels') },
              ...activeHostels.map(h => ({ value: h.id, label: h.name })),
            ]}
          />
        )}
      </div>

      {(activeHostelId || isAll) && (
        <>
          {/* Operations Row */}
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-6">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-center">
              <p className="text-xl font-bold text-teal-600">{s.occupiedBeds}</p>
              <p className="text-xs text-gray-500 mt-1">{t('dashboard.occupiedBeds')}</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-center">
              <p className="text-xl font-bold text-green-600">{s.vacantBeds}</p>
              <p className="text-xs text-gray-500 mt-1">{t('dashboard.vacantBeds')}</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-center">
              <p className="text-xl font-bold text-amber-600">{s.reservedBeds}</p>
              <p className="text-xs text-gray-500 mt-1">{t('dashboard.expectedCheckins')}</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-center">
              <p className="text-xl font-bold text-gray-600">{s.checkedOutTenants}</p>
              <p className="text-xs text-gray-500 mt-1">{t('dashboard.expectedCheckouts')}</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-center">
              <p className="text-xl font-bold text-blue-600">{s.reservedBeds}</p>
              <p className="text-xs text-gray-500 mt-1">{t('dashboard.waitingList')}</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-center">
              <p className="text-xl font-bold text-red-500">{s.cancelledTenants}</p>
              <p className="text-xs text-gray-500 mt-1">{t('dashboard.moveoutsThisMonth')}</p>
            </div>
          </div>

          {/* Charts & Metrics Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Bed Occupancy */}
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">{t('dashboard.bedOccupancy')}</h3>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={occupancyData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={4} dataKey="value">
                    {occupancyData.map((entry, idx) => <Cell key={idx} fill={entry.color} />)}
                  </Pie>
                  <Tooltip />
                  <Legend verticalAlign="bottom" height={30} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Today's Activity */}
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">{t('dashboard.todaysActivity')}</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-teal-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-teal-700">{s.reservedBeds}</p>
                  <p className="text-xs text-teal-600 mt-1">{t('dashboard.checkinsToday')}</p>
                </div>
                <div className="bg-gray-100 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-gray-700">{s.checkedOutTenants}</p>
                  <p className="text-xs text-gray-600 mt-1">{t('dashboard.checkoutsToday')}</p>
                </div>
                <div className="bg-amber-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-amber-700">{s.reservedBeds}</p>
                  <p className="text-xs text-amber-600 mt-1">{t('dashboard.reservations')}</p>
                </div>
                <div className="bg-red-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-red-700">{pending}</p>
                  <p className="text-xs text-red-600 mt-1">{t('dashboard.pendingApprovals')}</p>
                </div>
              </div>
            </div>

            {/* Rental Status */}
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">{t('dashboard.rentalStatus')}</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-green-50 rounded-xl p-4">
                  <p className="text-xs text-green-600 font-medium">{t('dashboard.collectedThisMonth')}</p>
                  <p className="text-xl font-bold text-green-700 mt-1">{fmtShort(s.collectedThisMonth)}</p>
                </div>
                <div className="bg-amber-50 rounded-xl p-4">
                  <p className="text-xs text-amber-600 font-medium">{t('dashboard.outstanding')}</p>
                  <p className="text-xl font-bold text-amber-700 mt-1">{fmtShort(s.pendingAmount)}</p>
                </div>
                <div className="bg-red-50 rounded-xl p-4">
                  <p className="text-xs text-red-600 font-medium">{t('dashboard.overdueRent')}</p>
                  <p className="text-xl font-bold text-red-700 mt-1">{fmtShort(s.overdueAmount)}</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-4">
                  <p className="text-xs text-blue-600 font-medium">{t('dashboard.collectionRate')}</p>
                  <p className="text-xl font-bold text-blue-700 mt-1">{s.collectionRate.toFixed(1)}%</p>
                </div>
              </div>
            </div>

            {/* Complaint Overview */}
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">{t('dashboard.complaintOverview')}</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-red-50 rounded-xl p-4">
                  <p className="text-xs text-red-600 font-medium">{t('dashboard.criticalComplaints')}</p>
                  <p className="text-xl font-bold text-red-700 mt-1">{s.criticalComplaints}</p>
                </div>
                <div className="bg-amber-50 rounded-xl p-4">
                  <p className="text-xs text-amber-600 font-medium">{t('dashboard.pendingOver3Days')}</p>
                  <p className="text-xl font-bold text-amber-700 mt-1">{s.complaintsPending3Days}</p>
                </div>
                <div className="bg-green-50 rounded-xl p-4">
                  <p className="text-xs text-green-600 font-medium">{t('dashboard.resolvedThisWeek')}</p>
                  <p className="text-xl font-bold text-green-700 mt-1">{s.resolvedThisWeek}</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-4">
                  <p className="text-xs text-blue-600 font-medium">{t('dashboard.avgResolution')}</p>
                  <p className="text-xl font-bold text-blue-700 mt-1">{s.avgResolutionTime.toFixed(1)}d</p>
                </div>
              </div>
            </div>

            {/* Revenue Trend */}
            {revenueData.length > 0 && (
              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 lg:col-span-2">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">{t('dashboard.revenueTrend')}</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={revenueData}>
                    <defs><linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#0d9488" stopOpacity={0.3} /><stop offset="95%" stopColor="#0d9488" stopOpacity={0} /></linearGradient></defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(value: number) => [fmt(value), t('dashboard.revenue')]} />
                    <Area type="monotone" dataKey="amount" stroke="#0d9488" strokeWidth={2} fill="url(#revGrad)" dot={{ r: 3, fill: '#0d9488' }} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Occupancy Trend */}
            {occupancyTrendData.length > 0 && (
              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 lg:col-span-2">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">{t('dashboard.occupancyTrend')}</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={occupancyTrendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                    <Tooltip formatter={(value: number) => [`${value.toFixed(1)}%`, t('dashboard.occupancyRate')]} />
                    <Line type="monotone" dataKey="occupancy" stroke="#0d9488" strokeWidth={2} dot={{ r: 3, fill: '#0d9488' }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Financial Snapshot */}
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 mb-6">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
              <TrendingUp size={14} className="text-teal-500" /> {t('dashboard.financialSnapshot')}
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <div className="bg-teal-50 rounded-lg p-3 text-center">
                <p className="text-xs text-teal-600">{t('dashboard.expectedIncome')}</p>
                <p className="text-lg font-bold text-teal-700">{fmtShort(totalToCollect)}</p>
              </div>
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <p className="text-xs text-green-600">{t('dashboard.collected')}</p>
                <p className="text-lg font-bold text-green-700">{fmtShort(s.collectedThisMonth)}</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-3 text-center">
                <p className="text-xs text-amber-600">{t('dashboard.outstanding')}</p>
                <p className="text-lg font-bold text-amber-700">{fmtShort(s.pendingAmount)}</p>
              </div>
              <div className="bg-red-50 rounded-lg p-3 text-center">
                <p className="text-xs text-red-600">{t('dashboard.overdueRent')}</p>
                <p className="text-lg font-bold text-red-700">{fmtShort(s.overdueAmount)}</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <p className="text-xs text-blue-600">{t('dashboard.collectionRate')}</p>
                <p className="text-lg font-bold text-blue-700">{s.collectionRate.toFixed(1)}%</p>
              </div>
            </div>
          </div>

          {/* Hostel Performance Table */}
          {multiHostel && (
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 mb-6">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">{t('dashboard.hostelPerformance')}</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left py-3 px-3 font-medium text-gray-500">{t('hostel.hostelName')}</th>
                      <th className="text-center py-3 px-3 font-medium text-gray-500">{t('dashboard.occupancyRate')}</th>
                      <th className="text-center py-3 px-3 font-medium text-gray-500">{t('dashboard.revenue')}</th>
                      <th className="text-center py-3 px-3 font-medium text-gray-500">{t('dashboard.pendingRent')}</th>
                      <th className="text-center py-3 px-3 font-medium text-gray-500">{t('nav.complaints')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeHostels.map(h => (
                      <tr key={h.id} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="py-3 px-3 font-medium text-gray-900">{h.name}</td>
                        <td className="text-center py-3 px-3 text-gray-700">-</td>
                        <td className="text-center py-3 px-3 text-gray-700">-</td>
                        <td className="text-center py-3 px-3 text-gray-700">-</td>
                        <td className="text-center py-3 px-3 text-gray-700">-</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Bottom Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">{t('dashboard.newTenants')}</h3>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="bg-teal-50 rounded-lg p-3">
                  <p className="text-lg font-bold text-teal-700">{s.activeTenants}</p>
                  <p className="text-xs text-teal-600">{t('dashboard.today')}</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-lg font-bold text-blue-700">-</p>
                  <p className="text-xs text-blue-600">{t('dashboard.yesterday')}</p>
                </div>
                <div className="bg-gray-100 rounded-lg p-3">
                  <p className="text-lg font-bold text-gray-700">-</p>
                  <p className="text-xs text-gray-600">{t('dashboard.thisWeek')}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">{t('dashboard.documentsExpiring')}</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2 bg-amber-50 rounded-lg px-3 py-2">
                  <FileText size={14} className="text-amber-500 shrink-0" />
                  <p className="text-xs text-amber-700">{t('dashboard.agreementExpiresSoon')}</p>
                </div>
                <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                  <FileText size={14} className="text-gray-400 shrink-0" />
                  <p className="text-xs text-gray-500">{t('dashboard.noPendingDocuments')}</p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
